import { useEffect, useRef } from "react";
import { useRoomContext } from "@livekit/components-react";
import { ConnectionState, RoomEvent } from "livekit-client";

import { OBSERVER_UI_ENABLED, OBSERVER_API_BASE_URL } from "./endpoints";

const STORAGE_PREFIX = "lk_observer_started:";

/**
 * Local dev only: POST to bot-backend `/observer/sessions/start` once the LiveKit
 * room is connected. Set `REACT_APP_OBSERVER_ENABLED=true` and `REACT_APP_OBSERVER_API_URL`
 * (e.g. http://localhost:8000).
 * Production should use a server-side webhook instead of the browser calling the observer.
 */
export default function LocalObserverBootstrap() {
  const room = useRoomContext();
  const roomRef = useRef(room);
  roomRef.current = room;

  useEffect(() => {
    if (!OBSERVER_UI_ENABLED || typeof window === "undefined") {
      return undefined;
    }
    const base = OBSERVER_API_BASE_URL.trim();
    if (!base) {
      return undefined;
    }

    const apiBase = base.replace(/\/$/, "");

    /**
     * After leave + quick rejoin, sessionStorage may still hold an old sessionId while the
     * bot already removed that session (last participant left). Re-check GET /sessions/:id
     * and only skip when that session is still running.
     */
    const tryStart = async () => {
      const r = roomRef.current;
      if (r.state !== ConnectionState.Connected) return;
      const roomName = r.name;
      if (!roomName) return;

      const storageKey = STORAGE_PREFIX + roomName;
      const existing = window.sessionStorage.getItem(storageKey);
      if (existing) {
        try {
          const statusRes = await fetch(
            `${apiBase}/observer/sessions/${encodeURIComponent(existing)}`
          );
          if (statusRes.ok) {
            const row = await statusRes.json();
            if (row.running) return;
          }
        } catch {
          /* ignore — fall through and clear stale id */
        }
        window.sessionStorage.removeItem(storageKey);
      }

      const url = `${apiBase}/observer/sessions/start`;
      fetch(url, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          roomName,
          hidden: true,
          identity: `ui-observer-${String(roomName).slice(0, 48)}`,
        }),
      })
        .then(async (res) => {
          const text = await res.text();
          if (!res.ok) {
            throw new Error(text || res.statusText);
          }
          try {
            return text ? JSON.parse(text) : {};
          } catch {
            return {};
          }
        })
        .then((data) => {
          console.info("[LiveKit observer] session started", data);
          window.sessionStorage.setItem(storageKey, data.sessionId || "1");
        })
        .catch((err) => {
          console.warn("[LiveKit observer] POST failed (is bot-backend running?)", err);
        });
    };

    const onDisconnected = () => {
      const name = roomRef.current?.name;
      if (name) {
        window.sessionStorage.removeItem(STORAGE_PREFIX + name);
      }
    };

    const onConnected = () => {
      void tryStart();
    };

    room.on(RoomEvent.Connected, onConnected);
    room.on(RoomEvent.Disconnected, onDisconnected);
    void tryStart();

    return () => {
      room.off(RoomEvent.Connected, onConnected);
      room.off(RoomEvent.Disconnected, onDisconnected);
    };
  }, [room]);

  return null;
}
